import { useQuery, useQueryClient } from '@tanstack/react-query';

import useUserLocations from '@/hooks/useUserLocations';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';

import { processUserName } from '@/services/modelHelper';

import type {
  CertificatesDataOptions,
  CertificatesDataResult,
} from './certificates.types';
import {
  isActiveCertificate,
  selectIntersectionCount,
  selectPlanCertificateCount,
} from './utils';

import {
  getPlanReportOptions,
  getPlansSummaryOptions,
  getUserSummaryReportOptions,
} from '@/_generated/api-clients/xp';
import { useReportsPlansFocus } from '@/routes/Reports/state';

/**
 * Certificate data for `/reports` routes.
 *
 * **Counts** use `getUserSummaryReportOptions` (team-member-only),
 * `getPlansSummaryOptions` (plan-only + intersection, shared list cache).
 * **Fetches** use reports-domain endpoints (`/planReports`,
 * `/userSummaryReport`).
 */
export const useReportsCertificatesData = ({
  teamMemberId,
  planId,
  enabled = true,
}: CertificatesDataOptions): CertificatesDataResult => {
  const queryClient = useQueryClient();
  const [isLicenseeMode] = useReportsPlansFocus('licenseeModeToggle');
  const locationNumbers = useUserLocations({ isLicenseeMode });

  const hasBothIds = !!teamMemberId && isValidPlanId(planId ?? undefined);

  // ============================================================
  // PLAN COUNT (from plans summary list cache)
  // When both IDs are known, narrows to the intersection;
  // otherwise returns the plan-only activeCertificates total.
  // ============================================================

  const {
    data: planCount,
    isFetching: isFetchingPlan,
    isSuccess: isPlanReady,
  } = useQuery({
    ...getPlansSummaryOptions({
      query: { locationNumbers },
    }),
    enabled:
      enabled &&
      isValidPlanId(planId ?? undefined) &&
      locationNumbers.length > 0,
    select: data =>
      hasBothIds
        ? selectIntersectionCount(data, planId, teamMemberId, false)
        : selectPlanCertificateCount(data, planId),
  });

  // ============================================================
  // USER-BASED COUNT (per-user endpoint for compliance-aware count)
  // Uses getUserSummaryReportOptions so the compliance expiration
  // check matches the latestSummaryReports logic. Also pre-warms the
  // cache used by fetchCertificates.
  // ============================================================

  const {
    data: userCertificateCount,
    isFetching: isFetchingUsersSummary,
    isSuccess: isUsersSummaryReady,
  } = useQuery({
    ...getUserSummaryReportOptions({
      path: { userId: teamMemberId ?? '' },
      query: {
        locationNumber: isLicenseeMode ? locationNumbers[0] : undefined,
      },
    }),
    enabled: enabled && !!teamMemberId && !hasBothIds,
    select: data =>
      data.planStatus.filter(status =>
        isActiveCertificate(
          status.planStatus,
          status.latestCompletionReport,
          false,
        ),
      ).length,
  });

  // ============================================================
  // LAZY CERTIFICATE FETCH
  // ============================================================

  const fetchCertificates = async () => {
    if (teamMemberId) {
      const data = await queryClient.ensureQueryData(
        getUserSummaryReportOptions({
          path: { userId: teamMemberId },
          query: {
            locationNumber: isLicenseeMode ? locationNumbers[0] : undefined,
          },
        }),
      );

      return data.planStatus
        .filter(
          status =>
            (!planId || status.planId === planId) &&
            isActiveCertificate(
              status.planStatus,
              status.latestCompletionReport,
              false,
            ),
        )
        .map(status => ({
          planName: itemByLanguage(status.planName) ?? '',
          teamMemberName: processUserName(data.user),
          completionDate: status.latestCompletionReport?.completionDate ?? '',
          expirationDate: status.latestCompletionReport?.expirationDate,
        }))
        .toSorted((a, b) => a.planName.localeCompare(b.planName));
    }

    if (planId && isValidPlanId(planId)) {
      const data = await queryClient.ensureQueryData(
        getPlanReportOptions({
          path: { planId },
          query: { locationNumbers },
        }),
      );

      const planName = itemByLanguage(data.name) ?? '';
      return (
        data.planUserReports
          ?.filter(report =>
            isActiveCertificate(
              report.status,
              report.latestCompletionReport,
              false,
            ),
          )
          .map(report => ({
            planName,
            teamMemberName: processUserName(report.user),
            completionDate: report.latestCompletionReport?.completionDate ?? '',
            expirationDate: report.latestCompletionReport?.expirationDate,
          }))
          .toSorted((a, b) =>
            a.teamMemberName.localeCompare(b.teamMemberName),
          ) ?? []
      );
    }

    return [];
  };

  // ============================================================
  // DERIVED STATE
  // ============================================================

  const certificateCount =
    teamMemberId && !hasBothIds
      ? (userCertificateCount ?? 0)
      : (planCount ?? 0);

  const isFetchingCount =
    teamMemberId && !hasBothIds ? isFetchingUsersSummary : isFetchingPlan;

  const isCountReady =
    teamMemberId && !hasBothIds ? isUsersSummaryReady : isPlanReady;

  return {
    certificateCount,
    isFetchingCount,
    isCountReady,
    fetchCertificates,
  };
};
